import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react'
import { FeatherIcon } from '../../components/FeatherIcon'
import { useIsNarrow } from '../../hooks/useIsNarrow'
import { ConfirmPopover } from '../ConfirmPopover'
import { NewRow } from '../NewRow'
import { PanelSearch } from '../PanelSearch'
import { useDebounced } from '../useDebounced'
import { SortMenu } from '../SortMenu'
import { DENSITY_COL, useDensity } from '../useDensity'
import { useGridSelect } from '../useGridSelect'
import { useSelection } from '../useSelection'
import { useToast } from '../useToast'
import { useTranslation } from 'react-i18next'
import type { Page } from '../../crm/client/pager'
import { useLibraryDeps, type Badge } from '../LibraryDeps'
import { ListLoading } from '../ListLoading'
import { Sentinel } from '../Sentinel'
import { pageMemory } from '../pageMemory'
import { usePagedList } from '../usePagedList'
import {
  MobileActionBar,
  MobileGroup,
  MobileListScreen,
  MobileOpenHead,
  MobileSelectHead,
  SwitchCarousel,
  SwitchStack,
  type SwitchItem,
  type ThumbSpec,
} from '../mobile'
import { useCarousel } from '../useCarousel'
import { Composer } from '../../of/home/Composer'
import type { When } from '../../of/home/homeData'
import { NewMessage } from '../../of/messages/NewMessage'
import { createOfApi, OF } from '../../of/ofApi'
import { BadgeMenu, type BadgeState } from './BadgeMenu'
import { badgesFor, useBadgeCatalog } from './badges'
import { CategoryActions } from './CategoryActions'
import { CategoryMenu } from './CategoryMenu'
import { CategorySortMenu, type CategoryOrder, type CategorySort } from './CategorySortMenu'
import { useCategoryReorder } from './useCategoryReorder'
import { MediaTile } from './MediaTile'
import { MediaViewer } from './MediaViewer'
import { NotesEditor } from './NotesEditor'
import { PreviewThumb } from './PreviewThumb'
import { SendMenu, type SendTarget } from './SendMenu'
import { UploadTile } from './UploadTile'
import { useVaultUploads } from './useVaultUploads'
import { ACCEPT } from '../../bulkMedia/stage'
import { useVaultNotes } from './useVaultNotes'
import { ALL_MEDIA, allMediaOf, toCategory, toItem, totalOf, TYPE_ICON, type Category, type MediaType, type VaultItem } from './vaultData'
import '../library.css'
import '../../of/ofPages.css'
import '../../of/home/home.css'

const MEDIA_PAGE = 24
/** 50 categories took ~7 s live (2026-10-07); the old page used 20. */
const CATEGORY_PAGE = 20

interface Props {
  accountId: string
  canWrite: boolean
  /** The phone's bottom tab bar (the CRM pages), shown when not selecting. */
  mobileNav?: ReactNode
}

type ListPayload = { list?: Record<string, unknown>[]; hasMore?: boolean; nextOffset?: number; all?: Record<string, unknown> }

const CHIPS: ReadonlyArray<readonly [MediaType | null, string]> = [
  [null, 'vault.chip.all'],
  ['photo', 'vault.chip.photo'],
  ['gif', 'vault.chip.gif'],
  ['video', 'vault.chip.video'],
  ['audio', 'vault.chip.audio'],
]

type VaultSort = 'newest' | 'oldest' | 'liked'
const SORTS = [
  { key: 'newest', labelKey: 'vault.sort.newest' },
  { key: 'oldest', labelKey: 'vault.sort.oldest' },
  { key: 'liked', labelKey: 'vault.sort.liked' },
] as const
/** OnlyFans' own orders (verified live 2026-10-07); there is no client-side re-sort. */
const SORT_QUERY: Record<VaultSort, { field: string; sort: string }> = {
  newest: { field: 'recent', sort: 'desc' },
  oldest: { field: 'recent', sort: 'asc' },
  liked: { field: 'most-liked', sort: 'desc' },
}


const categoryId = (c: Category) => c.id
const itemId = (m: VaultItem) => m.id

/** The Vault: the Account's OnlyFans media library, browsed by Category (handoff-11 CRM Library). */
export function VaultPage({ accountId, canWrite, mobileNav }: Props) {
  const { t } = useTranslation('library')
  const { gateway, upload, badges: badgesApi } = useLibraryDeps()
  const [all, setAllRaw] = useState<Category | null>(() => pageMemory.vaultAll.get(accountId) ?? null)
  const setAll = (c: Category) => {
    pageMemory.vaultAll.set(accountId, c)
    setAllRaw(c)
  }
  const [q, setQ] = useState('')
  const query = useDebounced(q.trim())
  const [openId, setOpenIdRaw] = useState(ALL_MEDIA)
  const [type, setType] = useState<MediaType | null>(null)
  const [sort, setSort] = useState<VaultSort>('newest')
  const [density, setDensity] = useDensity()
  // Phone: 0 = the categories, 1 = one category; Select mode can be on with nothing picked.
  const mobile = useIsNarrow()
  const [level, setLevel] = useState<0 | 1>(0)
  const [selectMode, setSelectMode] = useState(false)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const setOpenId = (id: string) => {
    setOpenIdRaw(id)
    setType(null)
    setSelectMode(false)
    if (scrollRef.current) scrollRef.current.scrollTop = 0
  }

  const fetchCategories = useCallback(
    async (offset: number): Promise<Page<Category>> => {
      const p = await gateway<ListPayload>('/v1/vault/lists', {
        view: 'main',
        offset,
        limit: CATEGORY_PAGE,
        ...(query ? { query } : {}),
      })
      if (offset === 0 && !query) setAll(allMediaOf(p))
      return { list: (p.list ?? []).map(toCategory), hasMore: p.hasMore, nextOffset: p.nextOffset }
    },
    // accountId: another account is another vault.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gateway, accountId, query],
  )
  const cats = usePagedList(fetchCategories, categoryId, query ? null : `of-vault-cats:${accountId}`)
  const categories = cats.items

  const fetchMedia = useCallback(
    async (offset: number): Promise<Page<VaultItem>> => {
      const p = await gateway<ListPayload>('/v1/vault/media', {
        list_id: openId,
        offset,
        limit: MEDIA_PAGE,
        ...SORT_QUERY[sort],
        ...(type ? { type } : {}),
      })
      return { list: (p.list ?? []).map(toItem), hasMore: p.hasMore, nextOffset: p.nextOffset }
    },
    [gateway, openId, type, sort],
  )
  const media = usePagedList(fetchMedia, itemId, `of-vault-media:${accountId}:${openId}:${type ?? ''}:${sort}`)
  const items = media.items
  const { notes, save: saveNote, badges: itemBadges, setBadges: setItemBadges } = useVaultNotes(items)

  // Badges (CONTEXT.md → Badge): the Account's set, and which loaded items carry which.
  // A tick saves straight away; the shown state changes first and rolls back on failure.
  const catalog = useBadgeCatalog(badgesApi)
  const canTag = canWrite && catalog.available
  const badgesOf = (id: number) => badgesFor(itemBadges[id], catalog.badges)
  const stateFor =
    (ids: number[]) =>
    (b: Badge): BadgeState => {
      const n = ids.filter((id) => itemBadges[id]?.includes(b.id)).length
      return n === 0 ? 'off' : n === ids.length ? 'on' : 'mixed'
    }
  const toggleBadge = async (ids: number[], b: Badge, on: boolean) => {
    const before = Object.fromEntries(ids.map((id) => [id, itemBadges[id]]))
    setItemBadges((map) => {
      const next = { ...map }
      for (const id of ids) {
        const rest = (next[id] ?? []).filter((x) => x !== b.id)
        next[id] = on ? [...rest, b.id].sort((x, y) => x - y) : rest
      }
      return next
    })
    try {
      const keys = ids.map(String)
      await catalog.setItems(b.id, on ? keys : [], on ? [] : keys)
    } catch (e) {
      setItemBadges((map) => {
        const next = { ...map }
        for (const id of ids) {
          if (before[id]) next[id] = before[id]!
          else delete next[id]
        }
        return next
      })
      say(e instanceof Error ? e.message : t('error'))
    }
  }
  // A tile's hover 🏷 / ✎: a menu or note editor pinned to the viewport (tiles clip).
  const [quick, setQuick] = useState<{ id: number; kind: 'badges' | 'note'; top: number; left: number } | null>(null)
  const openQuick = (id: number, kind: 'badges' | 'note', at: HTMLElement) => {
    const r = at.getBoundingClientRect()
    const w = kind === 'badges' ? 260 : 320
    const h = kind === 'badges' ? 360 : 200
    const left = Math.max(8, Math.min(r.right - w, window.innerWidth - w - 8))
    const top = r.bottom + h + 8 > window.innerHeight ? Math.max(8, r.top - h - 4) : r.bottom + 4
    setQuick({ id, kind, top, left })
  }

  // Upload (ADR 0025): files go into the open Category — a custom one; system lists
  // such as Messages take nothing — or only into the Vault. Placeholder tiles follow
  // each file; when OnlyFans has it, the grid re-reads its first page.
  const canUpload = canWrite && Boolean(upload)
  const ups = useVaultUploads(upload, async () => {
    await media.reload()
  })
  const fileInput = useRef<HTMLInputElement | null>(null)
  const pickFiles = () => fileInput.current?.click()
  useEffect(() => {
    if (!ups.sending) return
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [ups.sending])

  const [creating, setCreating] = useState(false)
  const createCategory = async (name: string) => {
    try {
      const raw = await gateway<Record<string, unknown>>('/v1/vault/lists/create', { name })
      const created = toCategory({ ...raw, name: raw?.name ?? name })
      cats.setItems((list) => [created, ...list])
      setOpenId(created.id)
    } catch (e) {
      say(e instanceof Error ? e.message : t('error'))
      throw e
    }
  }

  const [viewing, setViewing] = useState<number | null>(null)
  const viewed = viewing === null ? undefined : items[viewing]
  const keys = useMemo(() => items.map((m) => m.id), [items])
  const sel = useSelection(keys, canWrite)
  const picked = sel.picked
  const selecting = canWrite && (selectMode || picked.length > 0)
  const drag = useGridSelect({ scrollRef, keys, picked, onPick: sel.replace, enabled: canWrite, selecting: mobile ? selecting : undefined })
  const carousel = useCarousel(mobile && level === 1 && !selecting)
  const [menu, setMenu] = useState<'hide' | 'add' | 'send' | 'story' | 'badges' | null>(null)
  const { say, node: toast } = useToast()
  // OnlyFans' vault "Add to" menu: a new post or message with the selection
  // attached (in place of the grid), a post in a new tab, or the story.
  const of = useMemo(() => createOfApi(gateway), [gateway])
  const [catMenu, setCatMenu] = useState<{ id: string; top: number; right: number } | null>(null)
  const [categorySort, setCategorySort] = useState<CategorySort>('default')
  const [categoryOrder, setCategoryOrder] = useState<CategoryOrder>('asc')
  const renameCategory = async (c: Category, name: string) => {
    await of.send('PATCH', `vault/lists/${c.id}`, `${OF}/my/vault/`, { name })
    cats.setItems((list) => list.map((x) => (x.id === c.id ? { ...x, name } : x)))
    say(t('vault.categoryRenamed'))
  }
  const deleteCategory = async (c: Category, clearMedia: boolean) => {
    await of.send('DELETE', `vault/lists/${c.id}`, `${OF}/my/vault/`, { clearMedia })
    cats.setItems((list) => list.filter((x) => x.id !== c.id))
    if (openId === c.id) setOpenId(ALL_MEDIA)
    say(t('vault.categoryDeleted', { name: c.name }))
  }
  const categoryIdsRef = useRef(categories.map((c) => c.id))
  categoryIdsRef.current = categories.map((c) => c.id)
  const applyCategorySort = async (nextSort: CategorySort, nextOrder: CategoryOrder) => {
    setCategorySort(nextSort)
    setCategoryOrder(nextOrder)
    try {
      await of.send('POST', 'vault/lists/sort', `${OF}/my/vault/`, {
        sort: nextSort,
        order: nextOrder,
        ...(nextSort === 'custom'
          ? {
              customOrder: categoryIdsRef.current.map((id) => {
                const n = Number(id)
                return Number.isFinite(n) ? n : id
              }),
            }
          : {}),
      })
      await cats.reload()
    } catch (e) {
      say(e instanceof Error ? e.message : t('error'))
    }
  }
  const reorderCategory = (from: number, to: number) => {
    if (from === to) return
    const list = categoryIdsRef.current
    const next = [...list]
    const [id] = next.splice(from, 1)
    if (!id) return
    next.splice(to, 0, id)
    categoryIdsRef.current = next
    cats.setItems((items) => {
      const byId = new Map(items.map((c) => [c.id, c]))
      return next.map((x) => byId.get(x)).filter(Boolean) as Category[]
    })
  }
  const commitCategoryOrder = async () => {
    // OF POST /vault/lists/sort requires sort | order | customOrder (not listIds).
    const customOrder = categoryIdsRef.current.map((id) => {
      const n = Number(id)
      return Number.isFinite(n) ? n : id
    })
    try {
      setCategorySort('custom')
      setCategoryOrder('asc')
      await of.send('POST', 'vault/lists/sort', `${OF}/my/vault/`, {
        sort: 'custom',
        order: 'asc',
        customOrder,
      })
      say(t('vault.categoryMoved'))
    } catch (e) {
      await cats.reload()
      say(e instanceof Error ? e.message : t('error'))
    }
  }
  // Drag to reorder when not filtering; rename/delete via right-click / long-press.
  const catDrag = useCategoryReorder({
    enabled: canWrite && !query,
    getIds: () => categoryIdsRef.current,
    onReorder: reorderCategory,
    onCommit: () => void commitCategoryOrder(),
  })
  const openCategoryMenu = (id: string, row: HTMLElement) => {
    const r = row.getBoundingClientRect()
    setCatMenu({ id, top: r.bottom - 4, right: Math.max(8, window.innerWidth - r.right) })
  }
  const categoryMenuFor = (c: Category) =>
    catMenu && catMenu.id === c.id ? (
      <CategoryActions
        category={c}
        anchor={{ top: catMenu.top, right: catMenu.right }}
        onRename={(name) => renameCategory(c, name)}
        onDelete={(clear) => deleteCategory(c, clear)}
        onClose={() => setCatMenu(null)}
      />
    ) : null
  const categorySortControl = canWrite ? (
    <CategorySortMenu
      sort={categorySort}
      order={categoryOrder}
      onChange={(nextSort, nextOrder) => void applyCategorySort(nextSort, nextOrder)}
      onConfigure={() => void applyCategorySort('custom', 'asc')}
      triggerClassName={mobile ? 'lib-mbtn' : 'lib-icon-btn'}
    />
  ) : null
  const [composing, setComposing] = useState<Compose | null>(null)
  const pickedItems = () => items.filter((m) => picked.includes(m.id))
  const done = (text: string) => {
    setComposing(null)
    sel.clear()
    setSelectMode(false)
    say(text)
  }
  const sendTo = (target: SendTarget) => {
    setMenu(target === 'story' ? 'story' : null)
    if (target === 'post' || target === 'message') setComposing({ kind: target, items: pickedItems() })
    if (target === 'postTab') openComposeTab(pickedItems())
  }
  const addToStory = async () => {
    const [item] = pickedItems()
    setMenu(null)
    if (!item) return
    try {
      // A vault id is enough (verified live 2026-10-09); OnlyFans' own editor re-uploads a copy.
      await of.send('POST', 'stories', `${OF}/`, { mediaFiles: [item.id], rfTag: [], rfGuest: [], rfPartner: [] })
      done(t('vault.storyAdded'))
    } catch (e) {
      say(e instanceof Error ? e.message : t('error'))
    }
  }
  // A post opened in a new tab (OnlyFans' "Send to post in a new tab") arrives here.
  useEffect(() => {
    const handed = takeComposeHandoff()
    if (handed) setComposing({ kind: 'post', items: handed })
  }, [])
  const doneText = (kind: Compose['kind'], when: When) =>
    kind === 'post'
      ? t(when.kind === 'schedule' ? 'composer.scheduled' : when.kind === 'later' ? 'composer.savedLater' : 'composer.posted', { ns: 'ofHome' })
      : t(when.kind === 'schedule' ? 'scheduled' : when.kind === 'later' ? 'savedLater' : 'sent', { ns: 'ofMessage' })
  const composer = composing && (
    <div className="lib-compose">
      {composing.kind === 'post' ? (
        <Composer of={of} accountId={accountId} openOn={null} media={composing.items} onClose={() => setComposing(null)} onPosted={(_r, w) => done(doneText('post', w))} />
      ) : (
        <NewMessage of={of} accountId={accountId} media={composing.items} onClose={() => setComposing(null)} onSent={(_r, w) => done(doneText('message', w))} />
      )}
    </div>
  )
  // Hidden, not unmounted, while composing: Back returns to the same scroll spot.
  const pageStyle = { display: composing ? 'none' : 'contents' }
  const clearPick = () => {
    sel.clear()
    setMenu(null)
    setSelectMode(false)
  }

  const addTo = async (c: Category) => {
    const ids = picked
    setMenu(null)
    try {
      await gateway('/v1/vault/lists/add-media', { list_id: c.id, media_ids: ids })
      media.setItems((list) =>
        list.map((m) => (ids.includes(m.id) && !m.inCategories.includes(c.id) ? { ...m, inCategories: [...m.inCategories, c.id] } : m)),
      )
      sel.clear()
      setSelectMode(false)
      say(t('vault.added', { count: ids.length, name: c.name }))
    } catch (e) {
      say(e instanceof Error ? e.message : t('error'))
    }
  }

  const hide = async () => {
    const ids = picked
    setMenu(null)
    try {
      await gateway('/v1/vault/media/hide', { media_ids: ids })
      media.setItems((list) => list.filter((m) => !ids.includes(m.id)))
      sel.clear()
      setSelectMode(false)
      say(t('vault.hidden', { count: ids.length }))
    } catch (e) {
      say(e instanceof Error ? e.message : t('error'))
    }
  }

  const open = openId === ALL_MEDIA ? all : categories.find((c) => c.id === openId) ?? null
  const title = openId === ALL_MEDIA ? t('vault.allMedia') : open?.name ?? ''
  const total = open ? totalOf(open.counts) : 0
  const uploadTarget = open && open.id !== ALL_MEDIA && open.custom ? open : null
  const uploadLabel = uploadTarget ? t('vault.upload.toCategory', { name: uploadTarget.name }) : t('vault.upload.toVault')
  const shownUploads = ups.uploads.filter((u) => openId === ALL_MEDIA || u.target === openId)
  const onFiles = (files: File[]) => {
    if (!files.length) return
    const refused = ups.add(files, uploadTarget?.id ?? ALL_MEDIA, uploadTarget?.name ?? null)
    if (refused.length) say(t('vault.upload.refused', { count: refused.length, names: refused.join(', ') }))
  }
  const fileInputNode = canUpload && (
    <input
      ref={fileInput}
      type="file"
      multiple
      accept={ACCEPT}
      hidden
      data-testid="vault-upload-input"
      onChange={(e) => {
        onFiles([...(e.target.files ?? [])])
        e.target.value = ''
      }}
    />
  )
  const uploadTiles = (compact: boolean) =>
    shownUploads.map((u) => (
      <UploadTile key={u.key} upload={u} compact={compact} onRetry={() => void ups.retry(u.key)} onDismiss={() => ups.dismiss(u.key)} />
    ))

  const chips = (
    <div className="lib-chips" role="radiogroup" aria-label={t('vault.typeFilter')}>
      {CHIPS.map(([chipType, labelKey]) => {
        const on = type === chipType
        const n = open ? (chipType ? open.counts[chipType] : total) : null
        return (
          <button
            key={labelKey}
            type="button"
            role="radio"
            aria-checked={on}
            className={`lib-chip ${on ? 'is-on' : ''}`}
            onClick={() => setType(chipType)}
          >
            {t(labelKey)}
            {n !== null && <span className="lib-chip-n">{n.toLocaleString()}</span>}
          </button>
        )
      })}
    </div>
  )
  const addMenu = (
    <CategoryMenu
      title={t('vault.addTitle', { count: picked.length })}
      categories={categories}
      selection={items.filter((m) => picked.includes(m.id))}
      exceptId={openId}
      onPick={addTo}
      onClose={() => setMenu(null)}
    />
  )
  const sendMenu = <SendMenu selection={items.filter((m) => picked.includes(m.id))} newTab={!mobile} onPick={sendTo} onClose={() => setMenu(null)} />
  const storyConfirm = (
    <ConfirmPopover
      title={t('vault.storyTitle')}
      body={t('vault.storyBody')}
      confirmLabel={t('vault.storyConfirm')}
      onConfirm={() => void addToStory()}
      onCancel={() => setMenu(null)}
    />
  )
  const hideConfirm = (
    <ConfirmPopover
      title={t('vault.hideTitle', { count: picked.length })}
      body={t('vault.hideBody')}
      confirmLabel={t('vault.hide')}
      onConfirm={hide}
      onCancel={() => setMenu(null)}
    />
  )
  const bulkBadgeMenu = (
    <BadgeMenu
      title={t('vault.badges.bulkTitle', { count: picked.length })}
      catalog={catalog}
      stateOf={stateFor(picked)}
      onToggle={(b, on) => void toggleBadge(picked, b, on)}
      canManage={canTag}
      onClose={() => setMenu(null)}
    />
  )
  const quickPop =
    quick &&
    (quick.kind === 'badges' ? (
      <BadgeMenu
        catalog={catalog}
        stateOf={stateFor([quick.id])}
        onToggle={(b, on) => void toggleBadge([quick.id], b, on)}
        canManage={canTag}
        onClose={() => setQuick(null)}
        style={{ position: 'fixed', top: quick.top, left: quick.left, right: 'auto' }}
      />
    ) : (
      saveNote && (
        <>
          <div className="lib-backdrop" onClick={() => setQuick(null)} />
          <div className="lib-pop lib-note-pop" style={{ position: 'fixed', top: quick.top, left: quick.left, right: 'auto' }}>
            <NotesEditor value={notes[quick.id] ?? ''} onSave={(text) => saveNote(quick.id, text)} autoEdit onDone={() => setQuick(null)} />
          </div>
        </>
      )
    ))
  const viewer = viewed && (
    <MediaViewer
      items={items}
      onIndex={setViewing}
      onClose={() => setViewing(null)}
      hasMore={media.hasMore}
      onLoadMore={media.loadMore}
      index={viewing ?? 0}
      selected={picked.includes(viewed.id)}
      onToggle={canWrite ? () => sel.toggle(viewed.id) : undefined}
      mobile={mobile}
      notes={
        saveNote && {
          value: notes[viewed.id] ?? '',
          onSave: canWrite ? (text) => saveNote(viewed.id, text) : undefined,
        }
      }
      badges={
        catalog.available
          ? {
              catalog,
              badges: badgesOf(viewed.id),
              stateOf: stateFor([viewed.id]),
              onToggle: canTag ? (b, on) => void toggleBadge([viewed.id], b, on) : undefined,
            }
          : undefined
      }
    />
  )

  if (mobile) {
    const thumbOf = (c: Category): ThumbSpec => {
      const p = c.previews.find((x) => x.type !== 'audio') ?? c.previews[0]
      if (!p) return { icon: 'folder' }
      return p.type === 'audio' ? { icon: 'mic' } : { url: p.url, icon: 'folder' }
    }
    const switchItems: SwitchItem[] = [
      ...(all ? [{ id: ALL_MEDIA, label: t('vault.allMedia'), on: openId === ALL_MEDIA, thumb: { icon: 'grid', tint: true } }] : []),
      ...categories.map((c) => ({ id: c.id, label: c.name, on: c.id === openId, thumb: thumbOf(c) })),
    ]
    const switchTo = (id: string) => {
      carousel.setOpen(false)
      sel.clear()
      if (id !== openId) setOpenId(id)
    }
    const goOpen = (id: string) => {
      if (id !== openId) setOpenId(id)
      setLevel(1)
    }

    return (
      <div className="lib lib--m">
        {composer}
        <div style={pageStyle}>
          {level === 0 ? (
            <MobileListScreen
              title={t('vault.title')}
              addLabel={t('vault.newCategory')}
              onAdd={canWrite ? () => setCreating(true) : undefined}
              actions={
                <>
                  {categorySortControl}
                  {canUpload && (
                    <button
                      type="button"
                      className="lib-mbtn lib-mbtn--accent"
                      aria-label={t('vault.upload.toVault')}
                      title={t('vault.upload.toVault')}
                      onClick={() => {
                        // From the list of Categories, files go to All media.
                        goOpen(ALL_MEDIA)
                        pickFiles()
                      }}
                    >
                      <FeatherIcon name="upload" size={21} />
                    </button>
                  )}
                </>
              }
              searchLabel={t('vault.searchCategories')}
              query={q}
              onQuery={setQ}
            >
              {creating && <NewRow placeholder={t('vault.categoryName')} onCreate={createCategory} onDone={() => setCreating(false)} />}
              {all && !query && <CategoryRow label={t('vault.allMedia')} category={all} on={false} pinned mobile onOpen={() => goOpen(ALL_MEDIA)} />}
              <MobileGroup label={t('vault.categories')} count={categories.length} />
              {categories.map((c) => (
                <CategoryRow
                  key={c.id}
                  label={c.name}
                  category={c}
                  on={false}
                  mobile
                  dragging={catDrag.dragId === c.id}
                  drag={canWrite && !query ? catDrag.bindRow(c.id) : undefined}
                  onOpen={() => goOpen(c.id)}
                  onMenu={canWrite && c.custom ? (row) => openCategoryMenu(c.id, row) : undefined}
                  menu={categoryMenuFor(c)}
                />
              ))}
              <ListLoading loading={cats.loading} empty={!categories.length} />
              {cats.hasMore && <Sentinel onVisible={cats.loadMore} token={categories.length} />}
              {!cats.loading && !cats.hasMore && !categories.length && <p className="lib-empty">{t('mobile.nothingMatches')}</p>}
            </MobileListScreen>
          ) : (
            <>
              {selecting ? (
                <MobileSelectHead count={picked.length} onCancel={clearPick} onSelectAll={sel.selectAll} />
              ) : (
                <MobileOpenHead
                  title={title}
                  sub={t('vault.files', { count: total, n: total.toLocaleString() })}
                  onBack={() => {
                    clearPick()
                    carousel.setOpen(false)
                    setLevel(0)
                  }}
                  switcher={<SwitchStack items={switchItems} open={carousel.open} onToggle={carousel.toggle} label={t('mobile.switchCategory')} />}
                  onSelect={canWrite ? () => setSelectMode(true) : undefined}
                  more={
                    <>
                      {canUpload && (
                        <button type="button" className="lib-mbtn" aria-label={uploadLabel} title={uploadLabel} onClick={pickFiles}>
                          <FeatherIcon name="upload" size={20} />
                        </button>
                      )}
                      <SortMenu options={SORTS} value={sort} onChange={setSort} triggerClassName="lib-mbtn" iconSize={20} />
                    </>
                  }
                />
              )}
              <SwitchCarousel items={switchItems} open={carousel.open} onPick={switchTo} label={t('mobile.switchCategory')} />
              <div className="lib-mchips">{chips}</div>
              <div
                ref={scrollRef}
                className="lib-scroll lib-scroll--m"
                {...drag.rootProps}
                {...carousel.scrollProps}
                style={drag.rootProps.style}
              >
                <div className="lib-mgrid" role="listbox" aria-label={title} aria-multiselectable="true">
                  {uploadTiles(true)}
                  {items.map((m) => (
                    <MediaTile
                      key={m.id}
                      item={m}
                      compact
                      note={notes[m.id]}
                      badges={badgesOf(m.id)}
                      selected={picked.includes(m.id)}
                      onToggle={canWrite ? () => sel.toggle(m.id) : undefined}
                      onOpen={() => (selecting ? sel.toggle(m.id) : setViewing(items.indexOf(m)))}
                    />
                  ))}
                </div>
                {!media.loading && !media.hasMore && !items.length && !shownUploads.length && <p className="lib-empty">{t(type ? 'mobile.noFilesMatch' : 'mobile.categoryEmpty')}</p>}
                <ListLoading loading={media.loading} empty={!items.length} />
                {media.hasMore && <Sentinel onVisible={media.loadMore} token={items.length} />}
              </div>
            </>
          )}
        </div>
        {composing ? null : level === 1 && selecting ? (
          <MobileActionBar
            label={t('mobile.actions')}
            actions={[
              { icon: 'send', label: t('vault.sendTo'), disabled: !picked.length, onPress: () => setMenu('send') },
              { icon: 'folder-plus', label: t('vault.addTo'), disabled: !picked.length, onPress: () => setMenu('add') },
              ...(canTag ? [{ icon: 'tag', label: t('vault.badges.label'), disabled: !picked.length, onPress: () => setMenu('badges') }] : []),
              { icon: 'eye-off', label: t('vault.hide'), disabled: !picked.length, onPress: () => setMenu('hide') },
            ]}
          />
        ) : (
          mobileNav
        )}
        {menu === 'add' && addMenu}
        {menu === 'hide' && hideConfirm}
        {menu === 'send' && sendMenu}
        {menu === 'story' && storyConfirm}
        {menu === 'badges' && bulkBadgeMenu}
        {fileInputNode}
        {viewer}
        {toast}
      </div>
    )
  }

  return (
    <div className="lib">
      <aside className="lib-panel">
        <div className="lib-panel-head">
          <h1 className="lib-panel-title">{t('vault.title')}</h1>
          {categorySortControl}
          {canUpload && (
            <button type="button" className="lib-icon-btn" aria-label={uploadLabel} title={uploadLabel} onClick={pickFiles}>
              <FeatherIcon name="upload" size={17} />
            </button>
          )}
          {canWrite && (
            <button
              type="button"
              className="lib-icon-btn"
              aria-label={t('vault.newCategory')}
              title={t('vault.newCategory')}
              onClick={() => setCreating(true)}
            >
              <FeatherIcon name="plus" size={18} />
            </button>
          )}
        </div>
        <PanelSearch label={t('vault.searchCategories')} value={q} onChange={setQ} />
        {creating && <NewRow placeholder={t('vault.categoryName')} onCreate={createCategory} onDone={() => setCreating(false)} />}
        <nav
          className={`lib-rows ${catDrag.dragId ? 'is-reordering' : ''}`}
          aria-label={t('vault.categories')}
          title={canWrite && !query ? t('vault.dragReorder') : undefined}
        >
          {all && !query && (
            <CategoryRow
              label={t('vault.allMedia')}
              category={all}
              on={openId === ALL_MEDIA}
              onOpen={() => setOpenId(ALL_MEDIA)}
            />
          )}
          <div className="lib-group">
            <span>{t('vault.categories')}</span>
            <span>{categories.length}</span>
          </div>
          {categories.map((c) => (
            <CategoryRow
              key={c.id}
              label={c.name}
              category={c}
              on={openId === c.id}
              dragging={catDrag.dragId === c.id}
              drag={canWrite && !query ? catDrag.bindRow(c.id) : undefined}
              onOpen={() => setOpenId(c.id)}
              onMenu={canWrite && c.custom ? (row) => openCategoryMenu(c.id, row) : undefined}
              menu={categoryMenuFor(c)}
            />
          ))}
          <ListLoading loading={cats.loading} empty={!categories.length} />
          {cats.hasMore && <Sentinel onVisible={cats.loadMore} token={categories.length} />}
        </nav>
      </aside>
      <main className="lib-main">
        {composer}
        <div style={pageStyle}>
          <header className="lib-main-head">
            <h2 className="lib-main-title">{title}</h2>
            <span className="lib-main-sub">{t('vault.files', { count: total, n: total.toLocaleString() })}</span>
          </header>
          <div className="lib-toolbar">
            {chips}
            <span className="lib-spacer" />
            <SortMenu options={SORTS} value={sort} onChange={setSort} density={density} onDensity={setDensity} />
          </div>
          <div
            ref={scrollRef}
            className="lib-scroll"
            {...drag.rootProps}
            style={{ ['--lib-col' as string]: DENSITY_COL[density], ...drag.rootProps.style }}
          >
            {drag.box && (
              <div
                className="lib-dragbox"
                style={{ left: drag.box.left, top: drag.box.top, width: drag.box.width, height: drag.box.height }}
              />
            )}
            <div className="lib-grid lib-grid--media" role="listbox" aria-label={title} aria-multiselectable="true">
              {uploadTiles(false)}
              {items.map((m) => (
                <MediaTile
                  key={m.id}
                  item={m}
                  note={notes[m.id]}
                  badges={badgesOf(m.id)}
                  onBadges={canTag ? (at) => openQuick(m.id, 'badges', at) : undefined}
                  onEditNote={canWrite && saveNote ? (at) => openQuick(m.id, 'note', at) : undefined}
                  selected={picked.includes(m.id)}
                  onToggle={canWrite ? () => sel.toggle(m.id) : undefined}
                  onOpen={(e) => sel.clickWithKeys(m.id, e) || setViewing(items.indexOf(m))}
                />
              ))}
            </div>
            <ListLoading loading={media.loading} empty={!items.length} />
            {media.hasMore && <Sentinel onVisible={media.loadMore} token={items.length} />}
          </div>
          {picked.length > 0 && (
            <div
              className="lib-selbar lib-selbar--floating"
              role="toolbar"
              aria-label={t('mobile.actions')}
              title={t('vault.selectHint')}
            >
              <div className="lib-selbar-start">
                <button type="button" className="lib-selbar-clear" aria-label={t('clear')} onClick={clearPick}>
                  <FeatherIcon name="x" size={16} />
                </button>
                <span className="lib-selbar-n">
                  <span className="lib-selbar-frac" aria-hidden="true">
                    {picked.length} / {total.toLocaleString()}
                  </span>
                  <span>{t('selected', { count: picked.length })}</span>
                </span>
              </div>
              <div className="lib-selbar-actions">
                <button type="button" className="lib-selbar-icon" aria-label={t('selectAllShown')} title={t('selectAllShown')} onClick={sel.selectAll}>
                  <FeatherIcon name="check-square" size={18} />
                </button>
                {canTag && (
                  <div className="lib-anchor">
                    <button
                      type="button"
                      className="lib-selbar-icon"
                      aria-haspopup="menu"
                      aria-label={t('vault.badges.label')}
                      title={t('vault.badges.label')}
                      onClick={() => setMenu(menu === 'badges' ? null : 'badges')}
                    >
                      <FeatherIcon name="tag" size={18} />
                    </button>
                    {menu === 'badges' && bulkBadgeMenu}
                  </div>
                )}
                <div className="lib-anchor">
                  <button
                    type="button"
                    className="lib-selbar-icon"
                    aria-label={t('vault.hide')}
                    title={t('vault.hide')}
                    onClick={() => setMenu(menu === 'hide' ? null : 'hide')}
                  >
                    <FeatherIcon name="eye-off" size={18} />
                  </button>
                  {menu === 'hide' && hideConfirm}
                </div>
                <div className="lib-anchor">
                  <button
                    type="button"
                    className="lib-selbar-icon"
                    aria-haspopup="menu"
                    aria-label={t('vault.sendTo')}
                    title={t('vault.sendTo')}
                    onClick={() => setMenu(menu === 'send' ? null : 'send')}
                  >
                    <FeatherIcon name="send" size={18} />
                  </button>
                  {menu === 'send' && sendMenu}
                  {menu === 'story' && storyConfirm}
                </div>
                <div className="lib-anchor">
                  <button
                    type="button"
                    className="lib-btn lib-btn--primary lib-selbar-cta"
                    onClick={() => setMenu(menu === 'add' ? null : 'add')}
                  >
                    {t('vault.addTo')}
                  </button>
                  {menu === 'add' && addMenu}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      {fileInputNode}
      {quickPop}
      {viewer}
      {toast}
    </div>
  )
}

function CategoryRow({
  label,
  category,
  on,
  onOpen,
  onMenu,
  menu = null,
  drag,
  dragging = false,
  mobile = false,
  pinned = false,
}: {
  label: string
  category: Category
  on: boolean
  onOpen: () => void
  /** Right-click / long-press: rename & delete (no ⋮). */
  onMenu?: (row: HTMLElement) => void
  /** Popover rendered on this row (anchored to the folder). */
  menu?: ReactNode
  drag?: { 'data-cat-id': string; onPointerDown: (e: ReactPointerEvent) => void; onClickCapture: (e: ReactMouseEvent) => void }
  dragging?: boolean
  mobile?: boolean
  /** All media on the phone: a tinted grid icon, no previews. */
  pinned?: boolean
}) {
  const { t } = useTranslation('library')
  const c = category.counts
  const parts = (
    [
      ['photo', c.photo],
      ['video', c.video],
      ['gif', c.gif],
      ['audio', c.audio],
    ] as const
  ).filter(([, n]) => n > 0)
  return (
    <div
      className={`lib-row-wrap ${on ? 'is-on' : ''} ${dragging ? 'is-dragging' : ''} ${drag ? 'is-sortable' : ''}`}
      data-cat-id={drag?.['data-cat-id']}
      onPointerDown={drag?.onPointerDown}
      onClickCapture={drag?.onClickCapture}
      onContextMenu={
        onMenu
          ? (e) => {
              e.preventDefault()
              onMenu(e.currentTarget)
            }
          : undefined
      }
    >
      <button
        type="button"
        className={`lib-row ${mobile ? 'lib-row--m' : ''} ${pinned && mobile ? 'lib-row--pin' : ''} ${on ? 'is-on' : ''}`}
        aria-current={on ? 'true' : undefined}
        onClick={onOpen}
      >
        {pinned && mobile && (
          <span className="lib-row-icon" aria-hidden="true">
            <FeatherIcon name="grid" size={20} />
          </span>
        )}
        <span className="lib-row-text">
          <span className="lib-row-label">{label}</span>
          <span className="lib-row-meta">
            {parts.length
              ? parts.map(([type, n]) => (
                  <span key={type} className="lib-row-part" title={t(`vault.type.${type}`)}>
                    <FeatherIcon name={TYPE_ICON[type]} size={13} />
                    {n.toLocaleString()}
                  </span>
                ))
              : t('vault.empty')}
          </span>
        </span>
        {!(pinned && mobile) && (
          <span className="lib-row-prev" aria-hidden="true">
            {category.previews.slice(0, 3).map((p, i) => (
              <PreviewThumb key={i} preview={p} />
            ))}
          </span>
        )}
        {mobile && <FeatherIcon name="chevron-right" size={18} className="lib-row-chev" />}
      </button>
      {menu}
    </div>
  )
}

interface Compose {
  kind: 'post' | 'message'
  items: VaultItem[]
}

const HANDOFF = 'om-vault-compose:'

/** "New post in a new tab": the picked files go through localStorage (same origin), named in the URL. */
function openComposeTab(items: VaultItem[]) {
  const key = Math.random().toString(36).slice(2, 10)
  try {
    localStorage.setItem(HANDOFF + key, JSON.stringify(items))
  } catch {
    return
  }
  const url = new URL(window.location.href)
  url.searchParams.set('compose', key)
  window.open(url.toString(), '_blank', 'noopener')
}

/** The files a "new tab" post was opened with, once; the key and the URL parameter are dropped. */
function takeComposeHandoff(): VaultItem[] | null {
  const url = new URL(window.location.href)
  const key = url.searchParams.get('compose')
  if (!key) return null
  url.searchParams.delete('compose')
  window.history.replaceState(window.history.state, '', url.toString())
  try {
    const raw = localStorage.getItem(HANDOFF + key)
    localStorage.removeItem(HANDOFF + key)
    const items = raw ? (JSON.parse(raw) as VaultItem[]) : null
    return Array.isArray(items) && items.length ? items : null
  } catch {
    return null
  }
}
